import { Plus, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface Props {
  /** Para o `<Label htmlFor>` do `<Campo>` alcançar o controle de acrescentar. */
  id: string;
  sinonimos: string[];
  aoMudar: (sinonimos: string[]) => void;
  /** Não vira sinônimo de si mesmo: o servidor já casa o nome. */
  nomeDoItem: string;
  somenteLeitura?: boolean;
  /** O DTO recusa acima disto. Melhor travar o botão do que colher um 422. */
  maximo?: number;
}

/**
 * Chips de sinônimo — as OUTRAS grafias com que o clínico procura este item.
 *
 * É o mesmo padrão do `ApelidosDoAnalito` (catálogo de exames), promovido para
 * `shared/` na segunda e na terceira cópia: fármacos e diagnósticos precisam
 * exatamente disto, e o componente de lá é preso à grade de analitos (fala em
 * "apelido do Vertis" e vive dentro de uma linha da tabela).
 *
 * ⚠️ **Sinônimo não é enfeite: é o que a busca casa.** "HAC" só encontra
 * "Hiperadrenocorticismo" porque a sigla está aqui. Sem ele, quem digita a
 * sigla não acha nada, cadastra a segunda linha, e a coorte nasce partida em
 * duas.
 */
export function ChipsDeSinonimos({
  id,
  sinonimos,
  aoMudar,
  nomeDoItem,
  somenteLeitura = false,
  maximo = 20,
}: Props) {
  const [novo, setNovo] = useState('');

  function acrescentar() {
    const limpo = novo.trim();
    if (!limpo) return;

    // Comparação sem caixa: "cushing" e "Cushing" são o mesmo sinônimo, e a
    // busca no servidor também normaliza antes de casar.
    const igual = (a: string, b: string) => a.toLocaleLowerCase('pt-BR') === b.toLocaleLowerCase('pt-BR');

    const repetido = sinonimos.some((s) => igual(s, limpo));
    const eOProprioNome = igual(limpo, nomeDoItem.trim());

    if (!repetido && !eOProprioNome && sinonimos.length < maximo) aoMudar([...sinonimos, limpo]);
    setNovo('');
  }

  return (
    <div className="space-y-2">
      {sinonimos.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {sinonimos.map((sinonimo) => (
            <Badge key={sinonimo} variant="outline" className="gap-1 font-normal">
              {sinonimo}
              {!somenteLeitura && (
                <button
                  type="button"
                  aria-label={`Remover sinônimo ${sinonimo}`}
                  className="hover:text-destructive"
                  onClick={() => aoMudar(sinonimos.filter((s) => s !== sinonimo))}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {!somenteLeitura && sinonimos.length < maximo && (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            placeholder="Ex.: HAC, Cushing"
            className="h-8 text-sm"
            onKeyDown={(e) => {
              // Enter acrescenta o chip; sem o `preventDefault` ele enviaria o
              // formulário inteiro com o sinônimo ainda por digitar.
              if (e.key === 'Enter') {
                e.preventDefault();
                acrescentar();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0"
            aria-label="Acrescentar sinônimo"
            onClick={acrescentar}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
